import { X } from "lucide-react";
import { useCallback, useEffect, useId, useRef, type ReactNode } from "react";

import { cn } from "../lib/cn.js";

/**
 * Right-sliding sheet primitive built around the native <dialog>
 * element + showModal(), mirroring the existing Dialog ergonomics
 * (Escape + × close; focus is trapped; backdrop does not dismiss —
 * same accidental-miss posture as Dialog, canon 01-ui/dialog.md R5).
 *
 * Why hand-rolled rather than Radix/shadcn install: the existing
 * Dialog (packages/ui/src/dialog.tsx) is hand-rolled around <dialog>
 * for the same reasons (no extra deps, native focus trap). The
 * sheet shares that posture; it is the same primitive with a
 * different rest position (right edge of viewport rather than
 * vertical centre).
 */
interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  className?: string;
  /** Optional rendered alongside the close button (e.g. an action). */
  headerExtra?: ReactNode;
}

export function Sheet({ open, onClose, title, children, className, headerExtra }: SheetProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const ignoredCloseEvents = useRef(0);
  const titleId = useId();

  const closeWithoutNotify = useCallback((dialog: HTMLDialogElement) => {
    ignoredCloseEvents.current += 1;
    dialog.close();
  }, []);

  const handleNativeClose = useCallback(() => {
    if (ignoredCloseEvents.current > 0) {
      ignoredCloseEvents.current -= 1;
      return;
    }
    onClose();
  }, [onClose]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open) {
      if (!dialog.open) dialog.showModal();
    } else if (dialog.open) {
      closeWithoutNotify(dialog);
    }
  }, [closeWithoutNotify, open]);

  // Unmount cleanup — match Dialog: close natively without notifying
  // so route-change unmounts and StrictMode-double-mount don't loop.
  useEffect(() => {
    const dialog = dialogRef.current;
    return () => {
      if (dialog?.open) closeWithoutNotify(dialog);
    };
  }, [closeWithoutNotify]);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      onClose={handleNativeClose}
      // Pin to the right edge. We override the UA stylesheet centring
      // (which `Dialog` keeps) with explicit `right-0` + `top-0` so the
      // sheet docks. Width is responsive: full minus a gutter on small
      // screens, capped to `max-w-md` on larger viewports.
      className={cn(
        "fixed right-0 top-0 m-0 h-screen max-h-screen",
        "rounded-none border-l bg-surface p-0 shadow-2xl backdrop:bg-black/50",
        "w-[calc(100vw-2rem)] max-w-md",
        className,
      )}
    >
      <div className="flex h-screen max-h-screen flex-col">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-fg">
            {title}
          </h2>
          <div className="flex items-center gap-2">
            {headerExtra}
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-fg-subtle hover:text-fg-muted"
              aria-label="Close"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </dialog>
  );
}
